import { Backdrop, Box, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import ErrorBoundary from 'src/components/ErrorBoundary/ErrorBoundary';
import Etmf from 'src/components/eTMF/Etmf';
import Layout from 'src/components/layouts/DashboardLayout/DashboardLayout';
import 'src/muiLicense';
import { theme } from 'src/theme';
import { getPoratlHost } from './components/common/hooks/useUtils';
import { LoaderComponent } from './components/common/LoaderComponent';
import SnackbarComponent from './components/common/SnackbarComponent';
import Standalone from './components/common/Standalone';
import { useAppDispatch, useAppSelector } from './store';
import { setDocumentKey } from './store/preferences';
import { setPreferences, setToken, setUser } from './store/User';
import moment from 'moment';
import { SnackbarProvider } from 'notistack';

/**
 * App Container which load the Etmf component
 *
 * @returns ThemeProvider of Etmf App component
 */

const DEFAULT_EVENTS = [
  'mousemove',
  'keydown',
  'wheel',
  'DOMMouseScroll',
  'mousewheel',
  'mousedown',
  'touchstart',
  'touchmove',
  'MSPointerDown',
  'MSPointerMove',
  'visibilitychange'
];

export default function App() {
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const { message, severity, timestamp } = useAppSelector(state => state.settings.snackbar);
  const user = useAppSelector(state => state.user.user);
  const [waitForUserInfo, setUserInfoReceived] = useState(false);
  const dispatch = useAppDispatch();
  const siteToken = useAppSelector(state => state.user.user?.siteToken);

  const sendEventToPortal = () => {
    window.parent.postMessage({ action: 'USER_INFO' }, getPoratlHost() as string);
  };

  const sendIdleEventToPortal = (e: any) => {
    window.parent.postMessage({ action: 'UPDATE_IDLE_TIME' }, getPoratlHost() as string);
    if (e === 'mousedown') {
      window.parent.postMessage({ action: 'UPDATE_ON_CLICK' }, getPoratlHost() as string);
    }
  };

  useEffect(() => {
    sendEventToPortal();
    window.addEventListener('message', handleEvent);
    DEFAULT_EVENTS.forEach((e: any) => {
      document.addEventListener(e, () => sendIdleEventToPortal(e), {
        capture: true,
        passive: true
      });
    });
    return () => {
      window.removeEventListener('message', handleEvent);
      DEFAULT_EVENTS.forEach((e: any) => {
        document.removeEventListener(e, sendIdleEventToPortal, {
          capture: true
        });
      });
    };
  }, []);

  useEffect(() => {
    message && setIsSnackbarOpen(true);
  }, [message, timestamp]);

  const changeFontSize = (increase = true) => {
    const htmlElement = document.querySelector('html') as any;
    const style = window.getComputedStyle(htmlElement, null).fontSize;
    htmlElement.style.fontSize = parseFloat(style) + (increase ? 1 : -1) + 'px';
  };

  const updateToken = (token: string, siteToken: string) => {
    axios.defaults.headers.common['Authorization'] = `bearer ${token}`;
    axios.interceptors.request.use((config: any) => {
      return {
        ...config,
        headers: {
          ...config.headers,
          site_token: siteToken,
          user_date_time: moment().format()
        }
      };
    });
  };
  useEffect(() => {
    if (user?.sponsor) {
      axios.interceptors.request.use((config: any) => {
        return {
          ...config,
          headers: {
            ...config.headers,
            sponsor: user.sponsor,
            site_token: siteToken,
            user_date_time: moment().format()
          }
        };
      });
    }
  }, [user?.sponsor, siteToken]);
  const handleEvent = (event: MessageEvent) => {
    if ((event?.data?.source || '').indexOf('react-devtools-') > -1) {
      return;
    }
    if (event.origin !== getPoratlHost()) {
      //console.log('Message received from incorrect application.');
    } else {
      const message = event.data;
      switch (message.action) {
        case 'SPELL_CHECK':
          changeFontSize(message.increase);
          break;
        case 'USER_INFO':
          setUserInfoReceived(true);
          updateToken(message.user.token, message?.user?.siteToken);
          dispatch(setUser(message.user));
          dispatch(setDocumentKey(message.context));
          break;
        case 'UPDATE_TOKEN':
          updateToken(message.token.token, message.token.siteToken);
          dispatch(setToken(message.token));
          break;
        case 'PREFERENCES':
          dispatch(setPreferences(message.data));
          break;
        case 'OPEN_DOCUMENT_VIEWER':
          dispatch(setDocumentKey(message.context));
          break;
        default:
        //console.log('Message not found', message);
      }
    }
  };

  if (user === undefined || user === null)
    return (
      <>
        {waitForUserInfo && <Standalone />}
        <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={!waitForUserInfo}>
          <CircularProgress />
        </Backdrop>
      </>
    );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3} style={{ marginLeft: '220px' }}>
        <BrowserRouter>
          <Switch>
            <Route
              exact
              path="/"
              render={() => {
                return (
                  <ErrorBoundary>
                    <Layout>
                      <Box
                        sx={{
                          padding: '16px 0px 0px 233px'
                        }}
                      >
                        <Etmf />
                      </Box>
                    </Layout>
                  </ErrorBoundary>
                );
              }}
            />
            <Route
              exact
              path="/documentViewer/:id"
              render={props => {
                return (
                  <ErrorBoundary>
                    <Layout>
                      <Box
                        sx={{
                          padding: '80px 16px 16px 241px'
                        }}
                      >
                        <Etmf />
                      </Box>
                    </Layout>
                  </ErrorBoundary>
                );
              }}
            />
          </Switch>
        </BrowserRouter>
      </SnackbarProvider>
      <SnackbarComponent></SnackbarComponent>
      <LoaderComponent></LoaderComponent>
    </ThemeProvider>
  );
}
